// src/services/passwordService.js
import apiService from '../utils/apiInterceptor';

/**
 * Service for handling password-related API calls
 */
const passwordService = {
  /**
   * Update user password
   * @param {number|string} userId - User ID
   * @param {Object} passwordData - Password data object
   * @param {string} passwordData.current_password - Current password
   * @param {string} passwordData.password - New password
   * @param {string} passwordData.password_confirmation - Confirmation of new password
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  updatePassword: async (userId, passwordData) => {
    try {
      return await apiService.post(`/update-password/${userId}`, passwordData);
    } catch (error) {
      console.error(`Error updating password for user ${userId}:`, error);
      throw error;
    }
  },

  /**
   * Request password reset for user
   * @param {string} email - User email address
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  requestPasswordReset: async (email) => {
    try {
      return await apiService.post('/forgot-password', { email });
    } catch (error) {
      console.error(`Error requesting password reset for email ${email}:`, error);
      throw error;
    }
  },

  /**
   * Verify a password reset token
   * @param {string} token - Password reset token
   * @param {string} email - User email
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  verifyResetToken: async (token, email) => {
    try {
      return await apiService.post('/verify-reset-token', { token, email });
    } catch (error) {
      console.error(`Error verifying reset token for email ${email}:`, error);
      throw error;
    }
  },

  /**
   * Reset password with token fields used by the reset form
   * @param {string} email - User email
   * @param {string} token - Password reset token
   * @param {string} password - New password
   * @param {string} passwordConfirmation - Confirmation of new password
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  resetPassword: async (email, token, password, passwordConfirmation) => {
    try {
      return await apiService.post('/reset-password', {
        email,
        token,
        password,
        password_confirmation: passwordConfirmation,
      });
    } catch (error) {
      console.error(`Error resetting password for email ${email}:`, error);
      throw error;
    }
  },

  /**
   * Reset password with token
   * @param {Object} resetData - Password reset data
   * @param {string} resetData.email - User email
   * @param {string} resetData.token - Reset token
   * @param {string} resetData.password - New password
   * @param {string} resetData.password_confirmation - Password confirmation
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  resetPasswordWithToken: async (resetData) => {
    try {
      return await apiService.post('/reset-password', resetData);
    } catch (error) {
      console.error('Error resetting password with token:', error);
      throw error;
    }
  }
};

export default passwordService;
